import api from '@/services/api'
import axios from 'axios'

export type LocationGeoIP = {
  postalCode?: string;
  stateCode?: string;
  stateName?: string;
  countryCode?: string;
};

export class GeoLocateError extends Error {
  code?: number;

  private constructor(message: string, code?: number) {
    super(message);
    this.name = "GeoLocateError";
    this.code = code;
  }

  static notAuthenticated(): GeoLocateError {
    return new GeoLocateError("Authentication required for location lookup");
  }

  static invalidResponse(): GeoLocateError {
    return new GeoLocateError("Invalid response from locate service");
  }

  static apiError(code: number, message: string): GeoLocateError {
    return new GeoLocateError(`Locate error (${code}): ${message}`, code);
  }
}

export class LocationGeoIPService {
  static readonly shared = new LocationGeoIPService();

  cachedLocation: LocationGeoIP | null = null;
  private lastFetchDate: number | null = null;
  private inFlightPromise: Promise<LocationGeoIP> | null = null;
  private readonly cacheValidityDurationMs = 60 * 60 * 1000;
  private cacheVersion = 0;

  private constructor() {}

  /**
   * Best-effort warmup for Paddle/checkout location prefill. Does not reject.
   * Callers must not rely on cache being populated after this returns.
   */
  prefetchLocation(): void {
    void this.fetchLocation().catch(() => {
      /* locate is optional; network/API failures must not become unhandled rejections */
    });
  }

  async fetchLocation(): Promise<LocationGeoIP> {
    const now = Date.now();

    if (
      this.cachedLocation &&
      this.lastFetchDate &&
      now - this.lastFetchDate < this.cacheValidityDurationMs
    ) {
      return this.cachedLocation;
    }

    if (this.inFlightPromise) {
      return this.inFlightPromise;
    }

    const requestVersion = this.cacheVersion;
    const requestPromise = this.performRequest()
      .then((result) => {
        if (this.cacheVersion === requestVersion) {
          this.cachedLocation = result;
          this.lastFetchDate = Date.now();
        }
        return result;
      })
      .finally(() => {
        if (this.inFlightPromise === requestPromise) {
          this.inFlightPromise = null;
        }
      });

    this.inFlightPromise = requestPromise;
    return this.inFlightPromise;
  }

  clearCache(): void {
    this.cacheVersion += 1;
    this.cachedLocation = null;
    this.lastFetchDate = null;
    this.inFlightPromise = null;
  }

  private async performRequest(): Promise<LocationGeoIP> {
    try {
      const data = await api.locate();
      return this.normalizeLocateResponse(data);
    } catch (e) {
      if (e instanceof Error && e.message === "Authentication required") {
        throw GeoLocateError.notAuthenticated();
      }
      if (axios.isAxiosError(e)) {
        const status = e.response?.status ?? 0;
        const body = e.response?.data;
        let message = `HTTP ${status || "error"}`;
        if (typeof body === "string") {
          message = body;
        } else if (body && typeof body === "object") {
          const detail =
            (body as { detail?: unknown }).detail ??
            (body as { message?: unknown }).message ??
            (body as { error?: unknown }).error;
          if (typeof detail === "string") {
            message = detail;
          }
        }
        throw GeoLocateError.apiError(status, message);
      }
      throw e;
    }
  }

  private normalizeLocateResponse(data: unknown): LocationGeoIP {
    if (data === null || data === undefined) {
      throw GeoLocateError.invalidResponse();
    }
    if (typeof data !== "object") {
      throw GeoLocateError.invalidResponse();
    }

    const d = data as Record<string, unknown>;

    const nested = d.location ?? d.data;
    if (nested !== null && nested !== undefined && typeof nested === "object") {
      return this.normalizeLocateResponse(nested);
    }

    if (
      "postal" in d &&
      d.postal !== null &&
      typeof d.postal === "object" &&
      ("country" in d || "subdivisions" in d)
    ) {
      return this.parseMaxMindShapedResponse(data);
    }

    const pick = (...keys: string[]): string | undefined => {
      for (const key of keys) {
        const v = d[key];
        if (typeof v === "string" && v.trim() !== "") {
          return v;
        }
      }
      return undefined;
    };

    return {
      postalCode: pick(
        "postal_code",
        "postalCode",
        "postal",
        "zip",
        "zip_code",
        "zipCode"
      ),
      stateCode: pick(
        "state_code",
        "stateCode",
        "region_code",
        "regionCode",
        "subdivision_code",
        "subdivisionCode"
      ),
      stateName: pick(
        "state_name",
        "stateName",
        "region_name",
        "regionName",
        "subdivision_name",
        "subdivisionName"
      ),
      countryCode: pick(
        "country_code",
        "countryCode",
        "country_iso_code",
        "countryIsoCode"
      ),
    };
  }

  private parseMaxMindShapedResponse(data: unknown): LocationGeoIP {
    const json = (data ?? {}) as {
      postal?: { code?: unknown };
      subdivisions?: Array<{
        iso_code?: unknown;
        names?: Record<string, unknown>;
      }>;
      country?: { iso_code?: unknown };
    };

    const postalCode =
      typeof json.postal?.code === "string" ? json.postal.code : undefined;

    const firstSubdivision = Array.isArray(json.subdivisions)
      ? json.subdivisions[0]
      : undefined;

    const stateCode =
      typeof firstSubdivision?.iso_code === "string"
        ? firstSubdivision.iso_code
        : undefined;

    let stateName: string | undefined;
    if (firstSubdivision?.names && typeof firstSubdivision.names === "object") {
      const names = firstSubdivision.names;
      if (typeof names.en === "string") {
        stateName = names.en;
      } else {
        const firstValue = Object.values(names).find(
          (v): v is string => typeof v === "string"
        );
        stateName = firstValue;
      }
    }

    const countryCode =
      typeof json.country?.iso_code === "string"
        ? json.country.iso_code
        : undefined;

    return {
      postalCode,
      stateCode,
      stateName,
      countryCode,
    };
  }
}
